import { ActivatedRouteSnapshot } from '@angular/router';
import { BuilderUserContext } from '../../user';
import { SystemRoleBit } from '@gh/builder-shared-data';
import { getRouteQueryParams } from '@gh/common-shared';
import { ID } from '@gh/common-shared-data';

import { DEFAULT_FILTER_STATE, FilterNames } from './default-filter-state';

/** Query params owned by leads list filter/cursor trackers (not preserved across full page reload). */
export const LEADS_LIST_TRANSIENT_QUERY_PARAMS = [
  'filter',
  'page',
  'pageSize',
  'sortField',
  'sortDirection',
  'scroll',
  'focusedId',
];

const documentLoadedViaReload = readDocumentNavigationType() === 'reload';
let reloadResetHandled = false;

function readDocumentNavigationType(): PerformanceNavigationTiming['type'] | undefined {
  if (typeof performance === 'undefined') {
    return void 0;
  }
  if (performance.getEntriesByType) {
    const [navigation] = performance.getEntriesByType('navigation') as PerformanceNavigationTiming[];
    if (navigation?.type) {
      return navigation.type;
    }
  }
  const legacyType = (performance as Performance & { navigation?: { type?: number } }).navigation?.type;
  if (legacyType === 1) {
    return 'reload';
  }
  return void 0;
}

export function peekLeadsListPageReload(): boolean {
  return documentLoadedViaReload && !reloadResetHandled;
}

export function markLeadsListPageReloadHandled(): void {
  reloadResetHandled = true;
}

export function withoutHiddenLeadsFilter<T extends { showSuppressed?: boolean }>(filter: T): T {
  return { ...filter, showSuppressed: false };
}

export function rewriteFilterQueryWithoutHiddenLeads(rawFilter: string | undefined | null): string | undefined {
  if (!rawFilter) {
    return undefined;
  }
  try {
    const parsed = JSON.parse(rawFilter);
    if (!parsed?.showSuppressed) {
      return undefined;
    }
    return JSON.stringify(withoutHiddenLeadsFilter(parsed));
  } catch {
    return undefined;
  }
}

export function parseLeadsListFilterFromRoute(route: ActivatedRouteSnapshot): any | null {
  const params = getRouteQueryParams(route);
  if (params?.filter) {
    return withoutHiddenLeadsFilter(JSON.parse(params.filter));
  }
  return null;
}

/** Franchise office list: Salesperson role → current user id; otherwise All (''). */
export function getDefaultLeadsListSalesPersonId(userContext: BuilderUserContext): string {
  if (userContext.hasSystemRole(SystemRoleBit.SalesPerson)) {
    return String(userContext.data.id);
  }
  return '';
}

export function resolveLeadsListSalesPersonId(
  routeSalesPersonId: string | undefined,
  initFilterSalesPersonId: string | undefined,
  salesPersonsList: { id: ID }[],
  userContext: BuilderUserContext,
): string {
  const defaultId = getDefaultLeadsListSalesPersonId(userContext);
  let result = routeSalesPersonId || initFilterSalesPersonId || defaultId;
  if (routeSalesPersonId || initFilterSalesPersonId) {
    const exists = salesPersonsList.some(
      (item) => item.id === routeSalesPersonId || item.id === initFilterSalesPersonId,
    );
    if (!exists) {
      result = defaultId;
    }
  }
  return result;
}

/** Corporate/Master list: Salesperson role → [current user id]; otherwise All (['']). */
export function getDefaultCorpMasterSalesPersonIds(userContext: BuilderUserContext): string[] {
  if (userContext.hasSystemRole(SystemRoleBit.SalesPerson)) {
    return [String(userContext.data.id)];
  }
  return [''];
}

export function resolveCorpMasterSalesPersonIds(
  initFilterSalesPersonId: string[] | string | undefined,
  salesPersonsList: { id: ID }[],
  userContext: BuilderUserContext,
): string[] {
  const defaultIds = getDefaultCorpMasterSalesPersonIds(userContext);
  const normalize = (value: string[] | string | undefined): string[] | undefined => {
    if (value == null) {
      return void 0;
    }
    if (Array.isArray(value)) {
      return value.length > 0 ? value : void 0;
    }
    return value !== '' ? [value] : void 0;
  };
  let result = normalize(initFilterSalesPersonId) || defaultIds;
  const specificIds = result.filter((id) => id !== '');
  if (specificIds.length > 0) {
    const allExist = specificIds.every((id) => salesPersonsList.some((sp) => sp.id === id));
    if (!allExist) {
      result = defaultIds;
    }
  }
  return result;
}

/** Cleared date filter state (Create date / Last contact date + From/To). */
export const CORP_MASTER_LEADS_DATE_FILTER_DEFAULTS = {
  dateType: null,
  fromDate: null,
  toDate: null,
};

export function buildCorpMasterLeadsListDefaultFilter(userContext: BuilderUserContext): any {
  const filterName = userContext.corporateView ? FilterNames.leadsListCorp : FilterNames.leadsListMaster;
  const masterZone = userContext.corporateView
    ? { id: '', name: 'All' }
    : (userContext.masterZone ? userContext.masterZone : { id: '', name: 'All' });
  return {
    ...DEFAULT_FILTER_STATE[filterName],
    ...CORP_MASTER_LEADS_DATE_FILTER_DEFAULTS,
    salesPersonId: getDefaultCorpMasterSalesPersonIds(userContext),
    officeId: [''],
    masterZone,
    masterZoneId: userContext.corporateView ? '' : (masterZone.id || ''),
  };
}

export function toCorpOfficeFilterOptions<T extends { id: string | number; name?: string }>(offices: T[]): T[] {
  const real = (offices || []).filter((office) => String(office.id) !== '');
  if (real.length === 1) {
    return real;
  }
  return [{ id: '', name: 'All' } as T, ...real];
}

export function resolveCorpOfficeSelection(
  options: { id: string | number }[],
  savedIds?: string[] | string | null,
): string[] {
  const optionIds = (options || []).map((office) => String(office.id));
  const hasAll = optionIds.includes('');
  const realIds = optionIds.filter((id) => id !== '');

  const saved = savedIds == null
    ? []
    : (Array.isArray(savedIds) ? savedIds.map(String) : [String(savedIds)]);
  const valid = saved.filter((id) => optionIds.includes(id));
  if (valid.length > 0) {
    return valid;
  }
  if (!hasAll) {
    return realIds.length > 0 ? [realIds[0]] : [];
  }
  return [''];
}

/**
 * Normalizes multi-select dropdown selections for filters containing an "All" option (value '').
 *
 * Rules:
 * 1. Selecting "All" selects "All" (clears/deselects individual options).
 * 2. If the user deselects any individual option while "All" is active, "All" is automatically deselected.
 * 3. "All" only remains selected when every individual option is selected.
 * 4. If the user manually selects all individual options, "All" automatically becomes selected.
 * 5. If 0 items are selected, defaults back to "All" (['']).
 * 6. If All is not an available option, never write All; an empty select re-selects the single office.
 */
export function handleMultiSelectAllLogic(
  currentValues: (string | number)[] | undefined | null,
  previousValues: (string | number)[] | undefined | null,
  availableOptions: { id: string | number }[]
): string[] {
  const current = (currentValues || []).map(String);
  const previous = (previousValues || []).map(String);

  // Extract all non-empty individual option IDs
  const allIndividualIds = availableOptions
    .map(o => String(o.id))
    .filter(id => id !== '');

  const totalIndividualCount = allIndividualIds.length;
  const hasAllOption = (availableOptions || []).some((option) => String(option.id) === '');

  // Check if "All" (value '') was active in previous state
  const prevHadAll = previous.includes('') || (totalIndividualCount > 0 && previous.filter(id => id !== '').length === totalIndividualCount);
  const currHasAll = current.includes('');

  // Case 1: User explicitly clicked "All" (value '') when "All" was NOT previously active
  if (hasAllOption && currHasAll && !prevHadAll) {
    return [''];
  }

  // Extract currently selected individual IDs (excluding 'All')
  let selectedIndividuals = current.filter(id => id !== '');

  // Case 2: User clicked an individual option while "All" was active -> Deselect "All"
  if (prevHadAll && currHasAll && selectedIndividuals.length > 0 && selectedIndividuals.length < totalIndividualCount) {
    return selectedIndividuals;
  }

  // Case 3: All individual options were manually selected -> Automatically select "All"
  if (hasAllOption && totalIndividualCount > 0 && selectedIndividuals.length === totalIndividualCount) {
    return [''];
  }

  // Case 4: Nothing is selected -> Default back to "All", or the only office when All is hidden
  if (selectedIndividuals.length === 0) {
    if (hasAllOption) {
      return [''];
    }
    if (totalIndividualCount === 1) {
      return [allIndividualIds[0]];
    }
    const fallback = previous.filter((id) => id !== '');
    return fallback.length > 0 ? fallback : (allIndividualIds.length > 0 ? [allIndividualIds[0]] : []);
  }

  return selectedIndividuals;
}

